/**
 * 씬 생명주기 후크 (GameScene 추출) — 앱 활성/백그라운드 전환, 저장 flush,
 * CrazyGames 배너 gameplay pause, 일일 리셋/쿨타임 구동.
 *
 * GameScene의 셸 보조 책임을 얇은 coordinator로 분리해 GameScene을
 * 조립과 생명주기 중심으로 유지한다.
 */
import { App } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import {
  type AppActivity,
  decideLifecycleTransition,
} from "./appLifecycle.js";
import { DailyResetService } from "@yjsgames/daily-reset";
import { CooldownService, type CooldownEntry } from "@yjsgames/cooldown";
import { SaveDataResetStore } from "../systems/SaveDataResetStore.js";
import { SaveDataCooldownStore } from "../systems/SaveDataCooldownStore.js";

// ── 앱 활성/백그라운드 전환 ────────────────────────────────────────────────

export interface LifecycleTarget {
  /** 현재 활성 상태. */
  activity: AppActivity;
  /** 사운드 음소거/해제. */
  setMasterMuted(muted: boolean): void;
  /** 게임 루프 정지/재개. */
  setLoopSleep(sleep: boolean): void;
  /** 즉시 저장(debounce 우회). */
  flushSave(): void;
}

/**
 * 앱 활성/백그라운드 전환 적용 — 안드로이드 홈 이동 시 사운드 음소거 + 게임 루프 정지,
 * 복귀 시 해제/재개. 멱등 가드(changed)로 중복 신호를 무시한다.
 * 반환: 적용된 활성 상태.
 */
export function applyActivity(target: LifecycleTarget, next: AppActivity): AppActivity {
  const { actions, changed } = decideLifecycleTransition(next, target.activity);
  if (!changed) return target.activity;
  if (next === "background") target.flushSave();
  target.setMasterMuted(actions.mute);
  if (actions.pause) target.setLoopSleep(true);
  else target.setLoopSleep(false);
  return next;
}

// ── 저장/생명주기 이벤트 등록 ──────────────────────────────────────────────

export interface LifecycleHooksDeps {
  /** visibilitychange → 활성 전환. */
  onVisibilityChange: () => void;
  /** pagehide → 즉시 저장. */
  onPageHide: () => void;
  /** appStateChange → 활성 전환. */
  onAppStateChange: (isActive: boolean) => void;
}

export interface LifecycleHooksHandle {
  dispose(): void;
}

/**
 * 생명주기 저장 후크 등록 — visibilitychange, pagehide, Capacitor appStateChange.
 * SHUTDOWN 시 dispose()로 정리.
 */
export function registerLifecycleHooks(deps: LifecycleHooksDeps): LifecycleHooksHandle {
  let appStateHandle: PluginListenerHandle | undefined;
  let appStateRemoved = false;

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", deps.onVisibilityChange);
  }
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", deps.onPageHide);
  }

  void App.addListener("appStateChange", ({ isActive }) => {
    deps.onAppStateChange(isActive);
  }).then((handle) => {
    if (appStateRemoved) void handle.remove();
    else appStateHandle = handle;
  });

  return {
    dispose() {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", deps.onVisibilityChange);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("pagehide", deps.onPageHide);
      }
      appStateRemoved = true;
      void appStateHandle?.remove();
      appStateHandle = undefined;
    },
  };
}

// ── CrazyGames 배너 gameplay pause ─────────────────────────────────────────

export interface GameplayPauseTarget {
  setPaused(paused: boolean): void;
  pausePhysics(): void;
  resumePhysics(): void;
}

/** CrazyGames 배너 gameplay pause 상태 관리 — UI는 유지, 발사/물리/충돌만 정지. */
export function setCrazyGamesBannerPaused(
  target: GameplayPauseTarget,
  current: boolean,
  next: boolean,
): boolean {
  if (current === next) return current;
  target.setPaused(next);
  if (next) target.pausePhysics();
  else target.resumePhysics();
  return next;
}

// ── 일일 리셋 / 쿨타임 ────────────────────────────────────────────────────

/**
 * 일일 자정 리셋 구동 (@yjsgames/daily-reset) — boot들이 수집한 핸들러를 등록 후 check().
 * 부팅 직후 자정 통과분(미접속일 누적)을 첫 렌더 전에 반영한다.
 */
export function setupDailyReset(
  dailyResets: () => Record<string, number>,
  handlers: Map<string, () => void>,
  persist: () => void,
): DailyResetService {
  const service = new DailyResetService(
    () => Date.now(),
    new SaveDataResetStore(dailyResets),
  );
  for (const key of handlers.keys()) service.register({ key });
  service.onReset((event) => {
    handlers.get(event.key)?.();
    persist();
  });
  service.check();
  return service;
}

/**
 * 기간 쿨타임 구동 (@yjsgames/cooldown) — 등록/check 없이 호출 시점 판정.
 */
export function setupCooldown(
  cooldowns: () => Record<string, CooldownEntry>,
): CooldownService {
  return new CooldownService(
    () => Date.now(),
    new SaveDataCooldownStore(cooldowns),
  );
}
